import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface WhatsAppButtonProps {
  phoneNumber?: string;
  message?: string;
  position?: 'bottom-right' | 'bottom-left';
  showLabel?: boolean;
  className?: string;
  followScroll?: boolean;
}

export function WhatsAppButton({
  phoneNumber = '5561982321534',
  message = 'Olá! Tenho uma dúvida sobre minha assinatura no Ateliê PRO.',
  position = 'bottom-right',
  showLabel = true,
  className = '',
  followScroll = true,
}: WhatsAppButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Detecta scroll e mostra/oculta o botão
  useEffect(() => {
    let lastScrollTop = 0;
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      if (!followScroll) return;

      const scrollTop = window.scrollY;
      
      // Mostra o botão quando está scrollando
      setIsVisible(true);
      
      // Limpa timeout anterior
      clearTimeout(scrollTimeout);
      
      // Oculta depois de 1.5s de inatividade
      scrollTimeout = setTimeout(() => {
        setIsVisible(scrollTop > 100);
      }, 1500);

      lastScrollTop = scrollTop;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [followScroll]);

  const handleWhatsAppClick = () => {
    // Remove caracteres especiais do número
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // URL do WhatsApp Web com número e mensagem
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
    
    // Abre em nova aba
    window.open(whatsappUrl, '_blank');
  };

  const positionClasses = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
  };

  return (
    <div
      className={`fixed ${positionClasses[position]} z-40 flex items-end gap-2 transition-all duration-300 ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-32 pointer-events-none'
      } ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Label com tooltip */}
      {showLabel && isHovered && (
        <div className="animate-in fade-in slide-in-from-right-2 duration-200">
          <div className="bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg whitespace-nowrap text-sm font-medium">
            Dúvidas? Fale conosco!
            <div className="absolute bottom-0 right-0 translate-x-4 translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
          </div>
        </div>
      )}

      {/* Botão Principal */}
      <Button
        onClick={handleWhatsAppClick}
        className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full w-14 h-14 p-0 flex items-center justify-center group"
        title="Fale conosco via WhatsApp"
      >
        <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
      </Button>

      {/* Badge de notificação (opcional) */}
      <div className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
    </div>
  );
}

export default WhatsAppButton;
